import { useMemo, useState, type FormEvent } from "react";
import { Plus, Link2, Trash2, X } from "lucide-react";
import clsx from "clsx";
import { Button, Field, EmptyState, Icon, Modal, Picker, Spinner, DialogCancel } from "@cntrl/ui";
import { InviteLinkShare } from "../invites/InviteLinkShare.js";
import { InviteLinkDialog } from "../invites/InviteLinkDialog.js";
import { useOrgContext, useOrgCapability } from "../../app/orgContext.js";
import { useMe } from "../../app/hooks/useSession.js";
import { useOrg, orgInitials } from "../../app/hooks/useOrg.js";
import {
  useOrgTeamInvites,
  useInviteTeammate,
  useRevokeInvite,
  useOrgTeamMembers,
  useAssignArtist,
  useUnassignArtist,
  useChangeMemberRole,
  useRemoveOrgMember,
  type InviteIssued,
  type PendingInvite,
  type OrgSeatView,
} from "../../app/hooks/useOrgTeam.js";
import { orgRoleOptionsFor, roleLabelFrom, useSeatRoles } from "../../app/hooks/useSeatRoles.js";
import { ConfirmDialog } from "../shared/ConfirmDialog.js";
import { CrewProfileModal } from "../contacts/CrewProfileModal.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import styles from "./OrgTeamScreen.module.scss";

/** The pending-invites query, passed into its dialog rather than re-subscribed there (one cache key, one owner). */
type InvitesQuery = ReturnType<typeof useOrgTeamInvites>;

/** A band the org already works with — derived from the union of assignments across the roster (see below). */
interface RosterBand {
  bandId: string;
  bandName: string;
}

/** ISO → locale date (expiry display; date-only). */
function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** The routed page's H1 subtitle. The DIALOG carries no supporting line — a dialog header is the title alone
 *  (STYLE-GUIDE ruling 10, extended 2026-09-01), so this is the page treatment only. */
export const TEAM_BLURB = "Your teammates, their artist assignments, and pending invites.";

/**
 * The Team surface's BODY, and nothing else — the member roster with each seat's role pill, the "sees all
 * artists" admin chip, per-member artist-assignment chips + the "Assign artist" chip dropdown
 * (`POST /assignments`) and chip removal (`DELETE /assignments/{id}`), and a member Remove
 * (`DELETE /team/{seatId}`) hidden on the caller's own row (the service 409s on self-removal). Born-blank
 * empty, loading, and error states throughout.
 *
 * **It carries no card and no heading of its own** (PO, 2026-09-01). When this was two cards ("Members" and
 * "Pending invites") the chrome was doing real work — it said which list was which. With inviting and the
 * pending list moved into stacked dialogs there is ONE list left, and a titled card around it inside a dialog
 * called "Team" is a box labelled Members inside a box labelled Team. The routed page re-adds a surface,
 * because a bare list on the page canvas reads as unfinished; the dialog does not, because the dialog IS the
 * surface.
 *
 * Split out from the page so `TeamModal` and `/roster/team` render the ONE implementation, exactly as
 * `SettingsModal` and `/settings/*` share the settings tab bodies — a second chrome, never a second screen.
 *
 * **The whole surface is `roster:manage_seats`.** Its read is admin-gated server-side, so for a seat without
 * it there is nothing here to render: it used to mount anyway and answer with a bare 403 error card ("You do
 * not have permission to perform this action") under a "Team" heading, which is what the account menu's Team
 * entry led to. The affordance is now hidden for those seats, and reaching the URL directly gets this
 * designed state rather than an error that reads like a fault.
 */
export function OrgTeamPanel() {
  const { orgId } = useOrgContext();
  const canManage = useOrgCapability("roster:manage_seats");
  const canManageLinks = useOrgCapability("roster:manage_links");
  // Back for the ROLE PICKER: roles are scoped to the org's type, and the picker must not offer a label role
  // on a management org (the gate would 400). It is no longer used for LABELS — those come from the catalog.
  const org = useOrg(orgId);
  const me = useMe();
  // The read is roster:manage_seats-only — don't ask for it without that, or the surface renders its own 403
  // as a failure.
  const members = useOrgTeamMembers(canManage ? orgId : undefined);

  // Admins first (PO, 2026-09-01) — they are who you came to this surface to find, and the read returns seat
  // order, which is join order and means nothing to a reader. `sort` is stable, so everyone else keeps the
  // server's order rather than being re-shuffled by a second key nobody asked for.
  const memberItems = useMemo(
    () => [...(members.data?.items ?? [])].sort((a, b) => Number(!!b.canManageSeats) - Number(!!a.canManageSeats)),
    [members.data],
  );
  const myPersonId = me.data?.person?.personId;

  // The assignable-artist roster. There is no linked-bands list endpoint in this slice (the Phase-7
  // `GET /orgs/{orgId}/roster/overview` is where the full roster lands), so the picker sources the org's known
  // artists from the UNION of {bandId, bandName} across every member's current assignments — i.e. artists the
  // org already works with. A band that is linked but never yet assigned to anyone isn't discoverable here
  // until the Phase-7 roster read exists (documented limitation).
  const rosterBands = useMemo<RosterBand[]>(() => {
    const map = new Map<string, string>();
    for (const m of memberItems) {
      for (const a of m.assignments ?? []) {
        if (a.bandId) map.set(a.bandId, a.bandName ?? "Unnamed artist");
      }
    }
    return [...map.entries()]
      .map(([bandId, bandName]) => ({ bandId, bandName }))
      .sort((a, b) => a.bandName.localeCompare(b.bandName));
  }, [memberItems]);

  if (!canManage) {
    return (
      <EmptyState
        title="Only admins manage the team"
        description="Your organization's admins add teammates and assign them to artists. Ask one of them if you need a change."
      />
    );
  }

  if (members.isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading members…" />
      </div>
    );
  }

  if (members.isError) {
    return (
      <EmptyState
        title="Couldn't load members"
        description={members.error instanceof Error ? members.error.message : "Please try again."}
        action={<Button onClick={() => void members.refetch()}>Retry</Button>}
      />
    );
  }

  if (memberItems.length === 0) {
    return <EmptyState title="No teammates yet" description="Invite a teammate to build out your roster team." />;
  }

  return (
    // The list names itself, since the "Members" card head that used to name it is gone.
    <ul className={styles.rows} aria-label="Team members">
      {memberItems
        .filter((m) => m.seatId)
        .map((member) => (
          <OrgMemberRow
            key={member.seatId}
            member={member}
            orgId={orgId}
            orgType={org.data?.orgType}
            canManageSeats={canManage}
            canManageLinks={canManageLinks}
            rosterBands={rosterBands}
            isSelf={!!myPersonId && member.personId === myPersonId}
          />
        ))}
    </ul>
  );
}

/**
 * The Team surface's ACTIONS — the two doors off the member roster, and the stacked dialogs behind them.
 *
 * A separate component from the panel because the two surfaces PLACE them differently and neither placement
 * is inside the list: the dialog pins them in its footer, the page puts them beside its H1. The state they
 * own (which dialog is open) therefore has to live with the buttons, not with the roster.
 *
 * It renders NOTHING without `roster:manage_seats` — every action behind it is admin-only server-side, and an
 * affordance that always 403s is worse than an absent one. `TeamModal` re-checks the same capability to decide
 * whether the footer slot exists at all: a footer whose contents render null is still a bordered empty bar.
 */
export function OrgTeamActions() {
  const { orgId } = useOrgContext();
  const canManage = useOrgCapability("roster:manage_seats");
  const org = useOrg(orgId);
  const invites = useOrgTeamInvites(canManage ? orgId : undefined);

  const [inviting, setInviting] = useState(false);
  const [showingInvites, setShowingInvites] = useState(false);

  const pendingCount = invites.data?.items?.length ?? 0;

  if (!canManage) return null;

  return (
    <>
      {/* The door onto the pending list is ALWAYS here, count or not: an affordance that appears only when
          there is something behind it leaves "none pending" and "the list is somewhere else" identical. */}
      <Button variant="ghost" size="sm" onClick={() => setShowingInvites(true)}>
        Pending invites{pendingCount > 0 ? ` (${pendingCount})` : ""}
      </Button>
      <Button size="sm" onClick={() => setInviting(true)}>
        <Icon icon={Plus} size={16} /> Invite teammate
      </Button>

      {inviting && (
        <InviteTeammateModal orgId={orgId} orgType={org.data?.orgType} onClose={() => setInviting(false)} />
      )}

      {showingInvites && (
        <PendingInvitesModal
          orgId={orgId}
          invites={invites}
          canManage={canManage}
          onClose={() => setShowingInvites(false)}
        />
      )}
    </>
  );
}

/**
 * The org team PAGE (`/roster/team`) — the panel above under a page heading. It survives the move to a modal
 * for the same reason `/settings/*` did: a bookmark, and a surface the mobile shell can route to.
 *
 * It re-adds the paper surface the panel dropped, and hosts the actions beside its H1 where the dialog pins
 * them in its footer — the same content, placed the way each chrome places things.
 */
export function OrgTeamScreen() {
  const canManage = useOrgCapability("roster:manage_seats");
  return (
    <section className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Team</h1>
          <p className={styles.subtitle}>{TEAM_BLURB}</p>
        </div>
        {canManage && (
          <div className={styles.headActions}>
            <OrgTeamActions />
          </div>
        )}
      </header>
      <div className={styles.card}>
        <OrgTeamPanel />
      </div>
    </section>
  );
}

// ---- member row: role pill + admin chip + assignment chips + assign / remove ---------------------

function OrgMemberRow({
  member,
  orgId,
  orgType,
  canManageSeats,
  canManageLinks,
  rosterBands,
  isSelf,
}: {
  member: OrgSeatView;
  orgId: string;
  orgType: string | null | undefined;
  canManageSeats: boolean;
  canManageLinks: boolean;
  rosterBands: RosterBand[];
  isSelf: boolean;
}) {
  const seatRoles = useSeatRoles();
  const unassign = useUnassignArtist(orgId);
  const remove = useRemoveOrgMember(orgId);
  const [confirming, setConfirming] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  // The row opens the person's PROFILE — the same `CrewProfileModal` a crew row and a band-profile member
  // row open, stacked over this surface (PO, 2026-09-01). It is the ONE person dialog; an org-side copy is
  // what that invariant exists to prevent, and the dialog carries the shared orgs and artists on its rail.
  const [profileOpen, setProfileOpen] = useState(false);

  const name = member.displayName || member.email || "Unnamed teammate";
  const assignments = member.assignments ?? [];
  const assignedBandIds = new Set(assignments.map((a) => a.bandId).filter(Boolean) as string[]);

  function onConfirmRemove() {
    if (!member.seatId) return;
    setRemoveError(null);
    remove.mutate(member.seatId, {
      onSuccess: () => setConfirming(false),
      onError: (err) => setRemoveError(err.message),
    });
  }

  return (
    <li className={styles.memberRow}>
      <div className={styles.memberTop}>
        {/* Identity is the door, and it holds ONLY the face, the name and the email — the things that
            identify the person. The TITLE sits outside it, and both reasons are the same nesting:
            `Identity` renders a <button>, so a Picker inside it is a button inside a button — invalid, and
            the browser resolves the click to the outer one, so choosing a title opened the profile instead.
            It also picked up `.identity:hover .memberName`'s underline, because it sat inside the name.
            The remove button has always stayed outside for exactly this reason. */}
        <Identity
          // A row whose seat carries no person has nobody to open — a pending shape the read is not
          // expected to produce, but a control that opens nothing is worse than a plain row.
          onOpen={member.personId ? () => setProfileOpen(true) : undefined}
          label={`View ${name}'s profile`}
        >
          {/* The teammate's face. `orgInitials` still supplies the fallback — a person with no picture,
              or one the viewer may not resolve, keeps their monogram rather than a blank circle. */}
          <PersonAvatar
            personId={member.personId}
            avatarMediaId={member.avatarMediaId}
            initials={orgInitials(name)}
            className={styles.avatar}
          />
          <span className={styles.rowMain}>
            <span className={styles.memberName}>
              {name}
              {isSelf && <span className={styles.badge}>You</span>}
            </span>
            {member.email && <span className={styles.memberEmail}>{member.email}</span>}
          </span>
        </Identity>
        {canManageSeats && !isSelf ? (
          <RolePicker
            orgId={orgId}
            seatId={member.seatId!}
            memberName={name}
            title={member.titleKey}
            orgType={orgType}
          />
        ) : (
          <span className={styles.rolePill}>{roleLabelFrom(seatRoles.data?.orgRoles, member.titleKey)}</span>
        )}
        {!isSelf && (
          <button
            type="button"
            className={styles.remove}
            aria-label={`Remove ${name}`}
            onClick={() => setConfirming(true)}
          >
            <Icon icon={Trash2} size={20} />
          </button>
        )}
      </div>

      {/* The reach row. `sees_all_bands` admins reach every artist by policy, so instead of per-artist chips
          they carry ONE chip saying so — in the same row, in the same shape, because it answers the same
          question the chips answer ("which artists is this person on?"). It used to sit up in the meta line as
          a differently-coloured badge, which read as a second kind of fact. */}
      <div className={styles.chips}>
        {member.seesAllBands ? (
          <span className={clsx(styles.chip, styles.chipAll)}>
            <span className={styles.chipName}>Admin · sees all artists</span>
          </span>
        ) : (
          <>
            {assignments.map((a) => (
              <span key={a.assignmentId} className={styles.chip}>
                <span className={styles.chipName}>{a.bandName || "Unnamed artist"}</span>
                {canManageLinks && a.assignmentId && (
                  <button
                    type="button"
                    className={styles.chipRemove}
                    aria-label={`Unassign ${a.bandName || "artist"} from ${name}`}
                    disabled={unassign.isPending}
                    onClick={() => unassign.mutate(a.assignmentId as string)}
                  >
                    <Icon icon={X} size={16} />
                  </button>
                )}
              </span>
            ))}
            {canManageLinks && member.seatId && (
              <AssignArtistChip
                orgId={orgId}
                orgSeatId={member.seatId}
                memberName={name}
                rosterBands={rosterBands}
                assignedBandIds={assignedBandIds}
              />
            )}
          </>
        )}
      </div>

      {member.personId && profileOpen && (
        <CrewProfileModal
          personId={member.personId}
          fallback={{ name }}
          onClose={() => setProfileOpen(false)}
        />
      )}

      {confirming && (
        <ConfirmDialog
          title={`Remove ${name}?`}
          description="They lose their seat and every artist assignment immediately. Past data stays."
          confirmLabel="Remove teammate"
          error={removeError}
          loading={remove.isPending}
          onConfirm={onConfirmRemove}
          onCancel={() => setConfirming(false)}
        />
      )}
    </li>
  );
}

/** The row's identity half: a button that opens the person's profile, or a plain span when there is none. */
function Identity({
  onOpen,
  label,
  children,
}: {
  onOpen?: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return onOpen ? (
    <button type="button" className={styles.identity} aria-label={label} onClick={onOpen}>
      {children}
    </button>
  ) : (
    <span className={styles.identity}>{children}</span>
  );
}

// ---- "Assign artist" chip dropdown --------------------------------------------------------------

/**
 * Assign an artist to a teammate (`POST /assignments`) — a chip-shaped `Picker` sitting in the member's own
 * reach row, NOT a dialog (PO, 2026-09-01). Choosing is the whole interaction: there is one field and no
 * second decision to confirm, so a modal was chrome around a single press, and it took the assignment away
 * from the chips it lands among to make it.
 *
 * It is the `renderTrigger` shape the crew-position pill already uses, so the popup portals out of the row's
 * clipping context, keeps the full combobox keyboard contract, and closes on a CAPTURE-phase Escape — which
 * is what lets it live inside the Team dialog without the first Escape closing the dialog behind it.
 *
 * The band list is the org's known artists (see the OrgTeamPanel comment); one already assigned is DISABLED
 * rather than hidden, so the row still says the artist exists and is spoken for. An empty roster keeps its
 * explanation as the one (disabled) option, which a hidden control could not.
 *
 * There is no RESPONSIBILITY on an assignment any more (PO, 2026-09-01): the assignment itself is the fact the
 * roster acts on, and the label on top of it was a second vocabulary nobody read. `responsibilityLabel` stayed
 * in the hooks because the roster rail still rendered whatever historic rows carried — the rail stopped on
 * 2026-09-02 (a born-blank row now prints nothing), so it and `RESPONSIBILITIES` have NO caller left.
 */
/**
 * A member's role, as a chip dropdown in their row — the `AssignArtistChip` shape, and for the same reason:
 * one control plus a separate confirm button would mean the button confirms a choice already made.
 *
 * Rendered only for a seat that can manage seats, and never on your own row: the gate refuses a self-change
 * (demoting yourself is the cheapest way to lock yourself out of an org), so offering the control there would
 * be an affordance that can only fail. Everyone else sees the plain pill.
 *
 * Options are the org's OWN type only. The server re-stamps the seat's four capability flags from the new
 * role's tier, so picking an admin role here promotes the person — which is why the surrounding list re-sorts
 * admins first once the query settles.
 */
function RolePicker({
  orgId,
  seatId,
  memberName,
  title,
  orgType,
}: {
  orgId: string;
  seatId: string;
  memberName: string;
  title: string | null | undefined;
  orgType: string | null | undefined;
}) {
  const seatRoles = useSeatRoles();
  const change = useChangeMemberRole(orgId);
  const [error, setError] = useState<string | null>(null);

  // A member on a RETIRED title (ART-359) keeps it, so their own title stays in the list — otherwise the
  // Picker could not render its value. No other retired title is offered, and the server refuses a move onto one.
  const offered = orgRoleOptionsFor(seatRoles.data?.orgRoles, orgType);
  const options = title && !offered.some((r) => r.role === title)
    ? [...offered, ...(seatRoles.data?.orgRoles ?? []).filter((r) => r.role === title)]
    : offered;

  return (
    <>
      <Picker
        // Named per MEMBER — a dozen comboboxes all called "Role" is a screen reader with nothing to tell
        // them apart (the AssignArtistChip precedent).
        label={`Change ${memberName}'s role`}
        value={title ?? ""}
        disabled={change.isPending || options.length === 0}
        options={
          options.length === 0
            ? [{ value: "", label: "No roles available", disabled: true }]
            : options.map((r) => ({ value: r.role ?? "", label: r.label ?? "" }))
        }
        onChange={(next) => {
          if (!next || next === title) return;
          setError(null);
          change.mutate({ seatId, title: next }, { onError: (err) => setError(err.message) });
        }}
        renderTrigger={() => (
          <span className={styles.rolePill}>{roleLabelFrom(seatRoles.data?.orgRoles, title)}</span>
        )}
      />
      {/* Verbatim, in the row that failed — a refusal ("the account owner must hold an admin-tier role")
          explains itself, and a toast elsewhere would not say which member it was about. */}
      {error && (
        <span className={styles.error} role="alert">
          {error}
        </span>
      )}
    </>
  );
}

function AssignArtistChip({
  orgId,
  orgSeatId,
  memberName,
  rosterBands,
  assignedBandIds,
}: {
  orgId: string;
  orgSeatId: string;
  memberName: string;
  rosterBands: RosterBand[];
  assignedBandIds: Set<string>;
}) {
  const assign = useAssignArtist(orgId);
  const [error, setError] = useState<string | null>(null);

  const options =
    rosterBands.length === 0
      ? [
          {
            value: "",
            label: "No linked artists yet — an artist links your org from their Teams card",
            disabled: true,
          },
        ]
      : rosterBands.map((b) => ({
          value: b.bandId,
          label: b.bandName,
          disabled: assignedBandIds.has(b.bandId),
        }));

  return (
    <>
      <Picker
        // Named per MEMBER: every row carries one of these, and "Assign artist" alone would put a dozen
        // identically-named comboboxes on the surface with nothing to tell a screen reader them apart.
        label={`Assign artist to ${memberName}`}
        value=""
        disabled={assign.isPending}
        options={options}
        onChange={(bandId) => {
          if (!bandId) return;
          setError(null);
          assign.mutate({ orgSeatId, bandId }, { onError: (err) => setError(err.message) });
        }}
        renderTrigger={() => (
          <span className={styles.assignChip}>
            <Icon icon={Plus} size={16} /> Assign artist
          </span>
        )}
      />
      {/* The write's own failure, in the row that asked for it — the dialog used to hold this, and without a
          home here a 403/409 would land nowhere and read as a press that did nothing. */}
      {error && (
        <span className={styles.chipError} role="alert">
          {error}
        </span>
      )}
    </>
  );
}

// ---- invite a teammate ---------------------------------------------------------------------------

/**
 * Issue a seat invite (`POST /team/invites`) — a stacked dialog off the member roster, on the same `Modal`
 * primitive the assign dialog uses.
 *
 * Email + role, and nothing else: the TITLE field went with the assignment responsibility (PO, 2026-09-01) —
 * the role catalog already names what the seat is, and a free-text title beside it is a second answer to the
 * same question that no surface reads back.
 */
function InviteTeammateModal({
  orgId,
  orgType,
  onClose,
}: {
  orgId: string;
  orgType: string | null | undefined;
  onClose: () => void;
}) {
  const invite = useInviteTeammate(orgId);
  const seatRoles = useSeatRoles();
  const roles = orgRoleOptionsFor(seatRoles.data?.orgRoles, orgType);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>(roles[0]?.role ?? "");
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<InviteIssued | null>(null);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError("Enter the teammate's email.");
      return;
    }
    if (!role) {
      setError("Pick a role.");
      return;
    }
    invite.mutate(
      { email: email.trim(), title: role },
      {
        onSuccess: (result) => {
          setIssued(result);
          setEmail("");
        },
        onError: (err) => setError(err.message),
      },
    );
  }

  if (issued) {
    return (
      <Modal
        title="Invite teammate"
        size="sm"
        onClose={onClose}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Done
            </Button>
            <Button onClick={() => setIssued(null)}>Invite another</Button>
          </>
        }
      >
        <div className={styles.issued}>
          <p className={styles.issuedTitle}>Invite sent</p>
          {/* The server-built /join link (ART-239) — the wrapper's own origin is not the public one. */}
          {issued.joinUrl && (
            <InviteLinkShare url={issued.joinUrl} expiresOn={issued.expiresOn} recipient={issued.email} emailed />
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Invite teammate"
      size="sm"
      onClose={onClose}
      bodyAs="form"
      onSubmit={onSubmit}
      footer={
        <>
          <DialogCancel />
          <Button type="submit" loading={invite.isPending} disabled={!email.trim() || !role}>
            Send invite
          </Button>
        </>
      }
    >
      <div className={styles.dialogForm}>
        <Field
          label="Email"
          type="email"
          placeholder="teammate@org.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Picker
          id="invite-role"
          label="Role"
          value={role}
          onChange={setRole}
          options={
            roles.length === 0
              ? [{ value: "", label: "No roles available" }]
              : roles.map((r) => ({ value: r.role ?? "", label: r.label ?? "" }))
          }
        />
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ---- pending invites ------------------------------------------------------------------------------

/**
 * The invites that have been issued and not yet accepted — a stacked dialog rather than a second card under
 * the roster (PO, 2026-09-01). It is a place you go to check on or revoke something, not part of reading the
 * team, and as a card it took the same vertical weight as the roster while being empty most of the time.
 *
 * The query is the panel's, passed in: it already owns it for the head-button count, and a second
 * `useOrgTeamInvites` here would be a second subscriber to one key for no gain.
 */
function PendingInvitesModal({
  orgId,
  invites,
  canManage,
  onClose,
}: {
  orgId: string;
  invites: InvitesQuery;
  canManage: boolean;
  onClose: () => void;
}) {
  const seatRoles = useSeatRoles();
  const pending = invites.data?.items ?? [];
  return (
    <Modal
      title="Pending invites"
      size="md"
      onClose={onClose}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      {invites.isLoading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={20} label="Loading invites…" />
        </div>
      ) : invites.isError ? (
        <EmptyState
          title="Couldn't load invites"
          description={invites.error instanceof Error ? invites.error.message : "Please try again."}
          action={<Button onClick={() => void invites.refetch()}>Retry</Button>}
        />
      ) : pending.length === 0 ? (
        <EmptyState
          title="No pending invites"
          description="It's just you for now — invite a teammate to get started."
        />
      ) : (
        <ul className={styles.rows}>
          {pending.map((inv) => (
            <PendingRow
              key={inv.inviteId}
              invite={inv}
              roleText={roleLabelFrom(seatRoles.data?.orgRoles, inv.role)}   /* the invite's TITLE */
              canManage={canManage}
              orgId={orgId}
            />
          ))}
        </ul>
      )}
    </Modal>
  );
}

function PendingRow({
  invite,
  roleText,
  canManage,
  orgId,
}: {
  invite: PendingInvite;
  roleText: string;
  canManage: boolean;
  orgId: string;
}) {
  const revoke = useRevokeInvite(orgId);
  const [linkOpen, setLinkOpen] = useState(false);
  return (
    <li className={styles.row}>
      <span className={styles.rowMain}>
        <span className={styles.rowName}>{invite.email}</span>
        <span className={styles.rowSub}>
          {roleText} · expires {fmtDate(invite.expiresOn)}
        </span>
      </span>
      {canManage && invite.inviteId && (
        <>
          {/* ART-239: a fresh /join link — the stored token is a hash, so the old link can't be shown again. */}
          <Button variant="ghost" size="sm" onClick={() => setLinkOpen(true)}>
            <Icon icon={Link2} size={16} /> Copy link
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={styles.revoke}
            loading={revoke.isPending}
            onClick={() => revoke.mutate(invite.inviteId as string)}
          >
            <Icon icon={Trash2} size={16} /> Revoke
          </Button>
          {linkOpen && (
            <InviteLinkDialog
              target={{ kind: "org_seat", orgId, inviteId: invite.inviteId }}
              recipient={invite.email}
              onClose={() => setLinkOpen(false)}
            />
          )}
        </>
      )}
    </li>
  );
}
